<template>
  <Pane :value="isOpen"
        :tabs="tabs"
        current-tab="filter"
        temporary
        @input="onPaneValueChanged"
        @update:modelValue="onPaneValueChanged"
  >
    <template #content-filter>
      <div v-if="isOpen" class="pa-1">
        <div class="font-weight-semi-bold text--large mb-2">
          {{ $t('projects.ProjectsFiltersPane.projectType') }}
        </div>
        <v-row no-gutters class="mb-4">
          <v-col v-for="(projectType, index) in projectTypes"
                 :key="`projectType-${index}`"
                 cols="12"
          >
            <v-checkbox v-model="filters.projectType"
                        :label="$t(`common.projectTypes.${projectType}`)"
                        :value="projectType"
                        hide-details
                        multiple
                        density="compact"
                        class="mb-1"
                        color="darkblue-500"
            />
          </v-col>
        </v-row>

        <div class="font-weight-semi-bold text--large mb-2">
          {{ $t('projects.ProjectsFiltersPane.role') }}
        </div>
        <v-row no-gutters class="mb-4">
          <v-col v-for="(role, index) in roles"
                 :key="`role-${index}`"
                 cols="12"
          >
            <v-checkbox v-model="filters.role"
                        :label="$t(`projects.ProjectsFiltersPane.roles.${role}`)"
                        :value="role"
                        hide-details
                        multiple
                        density="compact"
                        class="mb-1"
                        color="darkblue-500"
            />
          </v-col>
        </v-row>

        <div class="font-weight-semi-bold text--large mb-3">
          {{ $t('projects.ProjectsFiltersPane.otherSettings') }}
        </div>
        <ProjectsFiltersMetadataGroupe @update:metadata="onMetadataUpdate"/>
          
        <template v-if="existingRoomsLabelsPending || existingRoomsLabelsError">
          <div v-if="existingRoomsLabelsPending" class="d-flex justify-center">
            <v-skeleton-loader type="text" />
          </div>
          <p v-else-if="existingRoomsLabelsError" class="text-error text-caption mt-2">
            {{ $t('projects.ProjectsFiltersPane.getAccessibleLabelsError') }}
          </p>
        </template>
        <template v-else>
          <div class="text-body-1 text--bold mb-2">
            {{ $t('projects.ProjectsFiltersPane.labels') }}
          </div>
          <AppMultiSelect
            v-model="filters.selectedLabels"
            :items="existingRoomsLabels"
            :item-title="item => item.label[$i18n.locale]"
            :item-value="item => item"
            :placeholder="$t('projects.ProjectsFiltersPane.labels')"
            data-locator="status-filter-select"
          >
            <template #no-data>
              <ProjectsFiltersNoData :label="$t('projects.ProjectsFiltersPane.labels')" />
            </template>
          </AppMultiSelect>
        </template>
        <div class="mb-4">
          <div class="text-body-1 text--bold mb-2">
            {{ $t('projects.ProjectsFiltersPane.creationDate') }}
          </div>
          <AppDateRangePicker
            :label="$t('projects.ProjectsFiltersPane.creationDate')"
            :model-value="filters.creationDate"
            @update:model-value="(value) => handleDateRangeChange(value, 'creationDate')"
          />
        </div>
        <div class="mb-8">
          <div class="text-body-1 text--bold mb-2">
            {{ $t('projects.ProjectsFiltersPane.dueDate') }}
          </div>
          <AppDateRangePicker
            :label="$t('projects.ProjectsFiltersPane.dueDate')"
            :model-value="filters.dueDate"
            @update:model-value="(value) => handleDateRangeChange(value, 'dueDate')"
          />
        </div>
        <v-divider class="my-10"/>
        <v-row>
          <v-col>
            <AppButton block
                       variant="tonal"
                       color="grey"
                       class="mr-2"
                       @click="deleteAllFilters"
            >
              {{ $t('common.clear') }}
            </AppButton>
          </v-col>
          <v-col>
            <AppButton color="primary"
                       block
                       class="ml-2"
                       @click="applyFilters"
            >
              {{ $t('common.apply') }}
            </AppButton>
          </v-col>
        </v-row>
      </div>
    </template>
  </Pane>
</template>

<script>
import { mapState, mapActions } from 'vuex'

import AppDateRangePicker from '@/common/AppDateRangePicker.vue'
import AppMultiSelect from '@/common/AppMultiSelect.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import Pane from '@/common/Pane.vue'
import { GET_EXISTING_ROOMS_LABELS } from '@/store/modules/rooms/action_types'
import { initialRoomFiltersPaneState } from '@/store/modules/rooms/state.js'

import ProjectsFiltersMetadataGroupe from './ProjectsFiltersMetadataGroupe.vue'
import ProjectsFiltersNoData from './ProjectsFiltersNoData.vue'

export default {
  name: 'ProjectsFiltersPane',
  components: {
    AppDateRangePicker,
    Pane,
    AppButton,
    ProjectsFiltersMetadataGroupe,
    ProjectsFiltersNoData,
    AppMultiSelect,
  },
  data () {
    return {
      tabs: [{ id: 'filter', title: this.$t('projects.ProjectsFiltersPane.filters') }],
      filters: null,
      projectTypes: ['project', 'dataroom'],
      roles: ['projectManager', 'guest'],
    }
  },
  computed: {
    ...mapState('rooms', [
      'filtersPane',
      'existingRoomsLabelsPending',
      'existingRoomsLabels',
      'existingRoomsLabelsError',
    ]),
    isOpen () {
      return this.filtersPane.isOpen
    },
  },
  watch: {
    isOpen (value) {
      if (value) {
        this.filters = { ...this.filtersPane.filters }
        this.GET_EXISTING_ROOMS_LABELS()
      }
    },
  },
  methods: {
    ...mapActions('rooms', [GET_EXISTING_ROOMS_LABELS]),
    onPaneValueChanged (value) {
      if (!value) {
        this.$store.commit('rooms/CLOSE_FILTER_PANE')
      }
    },
    handleDateRangeChange (value, property) {
      this.filters[property] = value
    },
    deleteAllFilters () {
      this.filters = { ...initialRoomFiltersPaneState }
      this.$store.commit('rooms/DELETE_FILTERS')
    },
    applyFilters () {
      this.$store.commit('rooms/SET_FILTERS', this.filters)
      this.onPaneValueChanged(false)
    },
    onMetadataUpdate(selectedMetadata) {
      if (!this.filters.metadata) {
        this.filters.metadata = {}
      }
      this.filters.metadata = { ...selectedMetadata }
    },
  },
}
</script>
<style scoped>
:deep(.v-selection-control__wrapper) {
  justify-content: flex-start;
}
</style>
